import React from "react";
import { Card } from "antd";
import { FileTextOutlined } from "@ant-design/icons";

// Read-only "Materials from Norm" rollup shown on the activity / phase / project
// summary. Per material: total requirement quantity + value summed across every
// norm-applied task below the node. Planning/report only — no apply/remove, no
// budget/procurement.

export interface RollupNormMaterial {
  materialId: number;
  materialName: string;
  uomName: string;
  totalQuantity: string;
  totalValue: string;
}

const fmtAmount = (n: number) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const NormMaterialRollupCard: React.FC<{
  materials: RollupNormMaterial[];
  loading?: boolean;
  title?: string;
}> = ({ materials, loading = false, title = "Materials from Norm" }) => {
  const rows = materials || [];
  const total = rows.reduce((s, m) => s + (Number(m.totalValue) || 0), 0);

  return (
    <Card
      size="small"
      style={{
        borderRadius: 12,
        background: "#f7f4fc",
        border: "1px solid #d3adf7",
        marginBottom: 20,
      }}
      headStyle={{
        background: "#f7f4fc",
        borderBottom: "1px solid #d3adf7",
        minHeight: 36,
      }}
      bodyStyle={{ padding: 12 }}
      title={
        <span style={{ fontSize: 13, fontWeight: 600, color: "#531dab" }}>
          <FileTextOutlined style={{ marginRight: 6 }} />
          {title}{" "}
          <span style={{ color: "#999", fontWeight: 400 }}>({rows.length})</span>
        </span>
      }
    >
      {loading && rows.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 4px" }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 4px" }}>
          No norm materials from tasks below.
        </div>
      ) : (
        <>
          <table
            style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}
          >
            <thead>
              <tr style={{ color: "#6b7280", textAlign: "left" }}>
                <th style={{ fontWeight: 500, padding: "2px 4px" }}>Material</th>
                <th
                  style={{
                    fontWeight: 500,
                    padding: "2px 4px",
                    textAlign: "right",
                  }}
                >
                  Total Qty
                </th>
                <th style={{ fontWeight: 500, padding: "2px 4px" }}>UOM</th>
                <th
                  style={{
                    fontWeight: 500,
                    padding: "2px 4px",
                    textAlign: "right",
                  }}
                >
                  Total Value
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr
                  key={m.materialId}
                  style={{ borderTop: "1px solid #ece6f6" }}
                >
                  <td style={{ padding: "3px 4px", color: "#1a1a2e" }}>
                    {m.materialName || `Material #${m.materialId}`}
                  </td>
                  <td
                    style={{
                      padding: "3px 4px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {m.totalQuantity}
                  </td>
                  <td style={{ padding: "3px 4px", color: "#6b7280" }}>
                    {m.uomName || "—"}
                  </td>
                  <td
                    style={{
                      padding: "3px 4px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtAmount(Number(m.totalValue))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #d3adf7",
              fontWeight: 600,
              color: "#1a1a2e",
              fontSize: 13,
            }}
          >
            <span>Total Requirement Value:</span>
            <span>{fmtAmount(total)}</span>
          </div>
        </>
      )}
    </Card>
  );
};

export default NormMaterialRollupCard;
